import { useTranslation } from "react-i18next";
import { cx } from "../cx";
import { InlineSpinner } from "../feedback/InlineSpinner";
import { PauseIcon, PlayIcon, RetryIcon } from "../icons";
import type { PlaybackStatus } from "./AudioProvider";

/**
 * The round play control. `idle` shows a triangle, `loading` a spinner,
 * `playing` pause bars, `failed` a retry arrow in danger; the click always
 * means "toggle", the caller decides what that does.
 */
export function PlayButton({
  status,
  onClick,
  size = "md",
  disabled = false,
  className = "",
}: {
  status: PlaybackStatus;
  onClick?: () => void;
  size?: "sm" | "md";
  disabled?: boolean;
  className?: string;
}) {
  const { t } = useTranslation("components");
  const label = {
    idle: t("play.play"),
    loading: t("play.loading"),
    playing: t("play.pause"),
    failed: t("play.failed"),
  }[status];
  const px = size === "sm" ? 24 : 36;
  const icon = size === "sm" ? 12 : 16;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      data-component="PlayButton"
      data-status={status}
      className={cx(
        "inline-flex shrink-0 items-center justify-center rounded-full border disabled:cursor-not-allowed disabled:opacity-60",
        status === "failed"
          ? "border-danger bg-danger-soft text-danger"
          : "border-accent bg-accent text-on-accent hover:bg-accent-hover",
        className,
      )}
      style={{ width: px, height: px }}
    >
      {status === "idle" ? <PlayIcon size={icon} /> : null}
      {status === "playing" ? <PauseIcon size={icon} /> : null}
      {status === "loading" ? (
        <InlineSpinner decorative size={icon} className="text-on-accent" />
      ) : null}
      {status === "failed" ? <RetryIcon size={icon} /> : null}
    </button>
  );
}
